import React from 'react';
import { PageHeader, useReveal, GlowDivider, StatCard } from '../components/Shared';

const TIMELINE = [
  { year:'2019', title:'Lab Founded',        desc:'D2S Lab established at the intersection of materials science, AI, and robotics.' },
  { year:'2020', title:'First AI Pipeline',  desc:'Deployed first closed-loop autonomous discovery pipeline for polymer electrolytes.' },
  { year:'2021', title:'Robotic Scale-Up',   desc:'Commissioned 6-axis robotic fabrication pods for continuous synthesis.' },
  { year:'2022', title:'DOE Partnership',    desc:'Awarded major Department of Energy grant for battery materials acceleration.' },
  { year:'2023', title:'50+ Publications',   desc:'Research team surpassed 50 peer-reviewed publications in top-tier journals.' },
  { year:'2024', title:'Global Expansion',   desc:'Opened collaborative nodes with 3 international research institutions.' },
];

const VALUES = [
  { icon:'⚡', title:'Speed',        desc:'We compress decade-long cycles into months through autonomous systems and AI.' },
  { icon:'🔬', title:'Rigor',         desc:'Every discovery is validated through systematic, reproducible experimental workflows.' },
  { icon:'🤝', title:'Collaboration', desc:'We partner openly with academia, national labs, and industry to maximize impact.' },
  { icon:'🌍', title:'Impact',        desc:'Our work targets materials for clean energy, healthcare, and sustainable manufacturing.' },
];

export default function About() {
  useReveal();
  return (
    <>
      <PageHeader
        tag="About D2S Lab"
        title="Who We"
        accent="Are"
        sub="A multidisciplinary team accelerating the journey from fundamental materials discovery to real-world manufacturing scale-up."
      />

      <GlowDivider />

      {/* Story Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="reveal-left">
              <span className="section-tag">Our Story</span>
              <h2 className="section-title">Built to Bridge the<br/><span className="text-gradient">Valley of Death</span></h2>
              <p className="text-sm font-light leading-relaxed mb-5" style={{color:'var(--text-2)'}}>
                The "valley of death" in materials science — the gap between laboratory discovery and commercial deployment — costs billions and decades. D2S Lab was founded to eliminate this gap entirely.
              </p>
              <p className="text-sm font-light leading-relaxed mb-8" style={{color:'var(--text-2)'}}>
                By combining AI-driven candidate screening, autonomous robotic synthesis, and multi-scale computational modeling into a single integrated platform, we reduce time-to-market from 10–20 years to under 2.
              </p>
              <div className="flex gap-4 flex-wrap">
                <a href="/contact" className="btn btn--primary">Partner With Us</a>
                <a href="/team"    className="btn btn--glass">Meet the Team</a>
              </div>
            </div>

            {/* Stats column */}
            <div className="reveal-right grid grid-cols-2 gap-4">
              {[{n:'10×',l:'Faster Discovery'},{n:'50+',l:'Publications'},{n:'12+',l:'Industry Partners'},{n:'4',l:'Global Nodes'}].map(({n,l})=>(
                <div key={l} className="glass-card text-center py-10 px-6">
                  <div className="font-display font-extrabold text-[2.6rem] tracking-[-0.05em] mb-2"
                    style={{background:'linear-gradient(135deg,#00ffff,#a78bfa)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text'}}>
                    {n}
                  </div>
                  <p className="font-mono text-[0.65rem] tracking-[3px] uppercase" style={{color:'var(--text-3)'}}>{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Values */}
      <section className="section section-alt">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">Core Values</span>
            <h2 className="section-title">What Drives <span className="text-gradient">Everything We Do</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map(({icon,title,desc},i)=>(
              <div key={title} className="glass-card reveal p-9" style={{transitionDelay:`${i*0.1}s`}}>
                <div className="text-4xl mb-5">{icon}</div>
                <h3 className="font-display font-bold text-lg tracking-[-0.03em] mb-3">{title}</h3>
                <p className="text-sm font-light leading-relaxed" style={{color:'var(--text-2)'}}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Timeline */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">Our Journey</span>
            <h2 className="section-title">Milestones That <span className="text-gradient">Define Us</span></h2>
          </div>
          <div className="relative">
            {/* Center line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2"
              style={{background:'linear-gradient(180deg,transparent,rgba(0,255,255,0.25) 20%,rgba(139,92,246,0.25) 80%,transparent)'}} />

            <div className="flex flex-col gap-10">
              {TIMELINE.map(({year,title,desc},i)=>(
                <div key={year} className={`flex flex-col lg:flex-row items-center gap-8 reveal ${i%2===0?'lg:flex-row':'lg:flex-row-reverse'}`}>
                  <div className={`lg:w-1/2 ${i%2===0?'lg:text-right lg:pr-12':'lg:text-left lg:pl-12'}`}>
                    <div className="glass-card p-8">
                      <span className="font-mono text-xs tracking-[3px] uppercase mb-3 block" style={{color:'#00ffff'}}>{year}</span>
                      <h3 className="font-display font-bold text-xl tracking-[-0.03em] mb-2">{title}</h3>
                      <p className="text-sm font-light leading-relaxed" style={{color:'var(--text-2)'}}>{desc}</p>
                    </div>
                  </div>
                  {/* Dot */}
                  <div className="hidden lg:flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
                    style={{background:'#00ffff',boxShadow:'0 0 16px rgba(0,255,255,0.6)',zIndex:1}} />
                  <div className="lg:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* CTA */}
      <section className="section section-alt">
        <div className="container text-center reveal">
          <span className="section-tag justify-center">Join Us</span>
          <h2 className="section-title mb-4">Ready to <span className="text-gradient">Collaborate?</span></h2>
          <p className="text-sm font-light leading-relaxed mx-auto mb-10 max-w-[480px]" style={{color:'var(--text-2)'}}>
            Whether you're from academia, a national lab, or industry — we want to hear from you.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a href="/contact" className="btn btn--primary">Get In Touch</a>
            <a href="/careers" className="btn btn--glass">View Open Roles</a>
          </div>
        </div>
      </section>
    </>
  );
}
